import hoistNonReactStatics from 'hoist-non-react-statics';
import SetRouterPageLoading from 'ia-react-core/~/actions/router/SetRouterPageLoading';
import React, { ComponentType, FC } from 'react';

import SetRouterPageLoaded from 'ia-react-core/~/actions/router/SetRouterPageLoaded';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { RouterState } from 'ia-react-core/~/interfaces/RouterState';
import Router from 'ia-react-core/~/services/router/Router';
import { LegacyHistory } from 'ia-react-core/~/interfaces/LegacyHistory';
import { upperFirst } from 'lodash';
import REDUX from '../../../constants/REDUX';
import compose from '../../../utils/components/_compose';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import injectReducer from '../../redux/injectReducer';
import injectSaga from '../../redux/injectSaga';

import withRouterReducer from './withRouter.reducer';
import withRouterSaga from './withRouter.saga';
import { injectQuery } from './withRouter.utils';

export interface WithRouterProps {
  name: string;
  scope: string[];
  store: AugmentedStore<any>;
}

export interface WithRouterOptions {
  basename?: string;
  router?: 'browser' | 'hash' | 'memory';
}

export interface WithRouterType<T> {
  (WrappedComponent: ComponentType<T>): ComponentType<T & WithRouterProps>;
  (options: WithRouterOptions): (WrappedComponent: ComponentType<T>) => ComponentType<T & WithRouterProps>;
}

export interface WithRouterMock {}

/**
 * @since 0.2.0
 * @see See [App configuration](/Devguide/App%20configuration)
 * @component
 */
export default function withRouter<T>(WrappedComponentOrConfig: ComponentType<T> | WithRouterOptions) {
  if (typeof WrappedComponentOrConfig === 'function' || (WrappedComponentOrConfig as any)?.type || (WrappedComponentOrConfig as any).render) {
    return withRouterConfig(WrappedComponentOrConfig as FC<WithRouterProps>);
  }
  return (WrappedComponent: FC<WithRouterProps>) => withRouterConfig(WrappedComponent, WrappedComponentOrConfig);

  function withRouterConfig(WrappedComponent: FC<WithRouterProps>, { basename, router = 'browser' }: WithRouterOptions = {}) {
    const BrowserImplementation = Router[`${upperFirst(router) as 'Browser' | 'Hash' | 'Memory'}Router`];

    const withBrowserRouter = (Wrapped: FC<WithRouterProps>) => (props: WithRouterProps) => (
      <BrowserImplementation basename={basename}><Wrapped {...props} /></BrowserImplementation>
    );

    const WithRouter = compose<any>(
      withBrowserRouter,
      Router.withRouter,
      injectReducer<WithRouterProps & { history: LegacyHistory }, RouterState, SetRouterPageLoading & SetRouterPageLoaded>({
        key: REDUX.ROUTE_REDUCER_KEY,
        initialState: ({ history }: { history: LegacyHistory }) => {
          const location = injectQuery(history.location as RouterState['location']);
          return ({
            location,
            action: history.action,
          });
        },
        reducer: withRouterReducer,
      }),
      injectSaga({ key: REDUX.ROUTE_REDUCER_KEY, saga: withRouterSaga }),
    )(WrappedComponent);

    WithRouter.displayName = getWrappedDisplayName('WithRouter', WrappedComponent);

    return hoistNonReactStatics(WithRouter, WrappedComponent);
  }
}
